import React, { useEffect, useState } from 'react'
import { toastError, toastSuccess, toastLoading, toastDismiss } from '../utils/toastHelper'
import { useSearchParams, useNavigate } from 'react-router-dom'

import { formatDateDisplay } from '@/utils/dateHelpers'
import { getStatusColors } from '@/utils/statusColorMap'
import { useTheme } from '@/hooks/useTheme'
import SimpleHeader from '@/components/common/SimpleHeader'
import Footer from '@/components/common/Footer'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import { useReservations } from '@/hooks/useReservations'
import { useShoppingCart } from '@/hooks/useShoppingCart'

interface Espacio {
  espacio_id: string
  espacio_nombre: string
  espacio_codigo: string
  unidad_reserva?: string
  monto_individual: number | null
  fecha_entrada?: string
  fecha_salida?: string
}

interface ReservaDetalle {
  id: string
  fecha_entrada: string
  fecha_salida: string
  cantidad_huespedes: number
  estado: string
  estado_pago: string
  monto_total: number
  solicitudes_especiales?: string
  fecha_creacion: string
  huesped_nombre: string
  huesped_email: string
  huesped_telefono?: string
  huesped_documento: string
  espacios: Espacio[]
}

const ReservationConfirmationPage: React.FC = () => {
  const navigate = useNavigate()
  const { colors } = useTheme()
  const [searchParams] = useSearchParams()
  const reservaId = searchParams.get('reserva_id')
  const { clearCart } = useShoppingCart()

  const [reservas, setReservas] = useState<ReservaDetalle[]>([])
  const [carritVaciado, setCarritoVaciado] = useState(false)

  // Obtener reservas por ID
  // El backend ahora retorna TODOS los espacios asociados (incluyendo los de reserva_espacios)
  const { data: response, isLoading, error } = useReservations(
    undefined,
    undefined,
    reservaId || undefined
  )

  useEffect(() => {
    if (response?.reservas && response.reservas.length > 0) {
      setReservas(response.reservas)
      // Vaciar carrito cuando se confirma el pago
      if (!carritVaciado) {
        clearCart()
        setCarritoVaciado(true)
      }
    } else if (response?.reservas && response.reservas.length === 0) {
      toastError('No se encontró la reserva')
      setTimeout(() => navigate('/'), 2000)
    }
  }, [response, navigate, clearCart, carritVaciado])

  useEffect(() => {
    if (error) {
      toastError('Error al cargar la reserva')
      setTimeout(() => navigate('/'), 2000)
    }
  }, [error, navigate])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Confirmación de Reserva"
          subtitle="Tu reserva ha sido confirmada exitosamente"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-6 text-lg text-gray-600 font-medium">
              Cargando detalles de tu reserva...
            </p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  if (reservas.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <SimpleHeader
          title="Confirmación de Reserva"
          subtitle="Tu reserva ha sido confirmada exitosamente"
        />
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full text-center">
            <p className="text-gray-600 mb-6">No se pudo cargar la información de la reserva.</p>
            <button
              onClick={() => navigate('/')}
              className="w-full btn-primary btn-lg"
            >
              🏠 Volver al Inicio
            </button>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // La reserva contiene los datos generales y todos los espacios en array "espacios"
  const reserva = reservas[0]
  const espacios = reserva.espacios || []
  // Usar montos individuales si existen, si no usar monto_total de la reserva
  const montoTotal = espacios.length > 0
    ? espacios.reduce((sum, e) => sum + (e.monto_individual || 0), 0)
    : reserva.monto_total

  const formatearEstado = (estado: string) => {
    return estado.charAt(0).toUpperCase() + estado.slice(1).replace('_', ' ')
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SimpleHeader
        title="✅ Confirmación de Reserva"
        subtitle="Tu reserva ha sido confirmada exitosamente"
      />

      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Card 1: ID de Reserva */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
              <p className="text-sm text-blue-600 font-medium">ID de Reserva</p>
              <p className="text-lg font-mono font-bold text-blue-900 break-all mt-1">
                {reserva.id}
              </p>
            </div>
          </div>

          {/* Card 2: Espacios Reservados */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Espacios Reservados</h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Renderizar cada espacio con sus propias fechas */}
              {(() => {
                return espacios.map((espacio) => {
                  // Usar fechas individuales del espacio si existen, si no usar fechas de reserva
                  const fechaEntrada = espacio.fecha_entrada || reserva.fecha_entrada
                  const fechaSalida = espacio.fecha_salida || reserva.fecha_salida

                  const entrada = new Date(fechaEntrada + 'T00:00:00')
                  const salida = new Date(fechaSalida + 'T00:00:00')
                  const diasDiferencia = Math.floor(
                    (salida.getTime() - entrada.getTime()) / (1000 * 60 * 60 * 24)
                  )
                  const duracion = espacio.unidad_reserva === 'dia' ? diasDiferencia + 1 : diasDiferencia

                  return (
                    <div key={espacio.espacio_id} className="border border-gray-200 rounded-lg p-6">
                      {/* Header del Espacio */}
                      <div className="mb-4">
                        <h3 className="text-xl font-bold text-gray-900">
                          {espacio.espacio_nombre}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Código: {espacio.espacio_codigo}
                        </p>
                      </div>

                      {/* Fechas y Duración - INDIVIDUALES POR ESPACIO */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-gray-600 font-medium mb-1">Entrada</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatDateDisplay(fechaEntrada)}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-gray-600 font-medium mb-1">Salida</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatDateDisplay(fechaSalida)}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs text-gray-600 font-medium mb-1">Duración</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {duracion} {espacio.unidad_reserva === 'dia' ? 'días' : 'noches'}
                          </p>
                        </div>
                      </div>

                      {/* Monto Individual del Espacio */}
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">Monto</p>
                        <p className="text-lg font-bold" style={{ color: colors.primary }}>
                          ${(espacio.monto_individual || 0).toLocaleString('es-CL')}
                        </p>
                      </div>
                    </div>
                  )
                })
              })()}

              {/* Mostrar Solicitudes Especiales una sola vez (al final) */}
              {reserva.solicitudes_especiales && (
                <div className="border border-gray-200 rounded-lg p-6">
                  <p className="text-xs text-gray-600 font-medium mb-2">Solicitudes Especiales</p>
                  <p className="text-sm text-gray-900 bg-gray-50 rounded p-2">
                    {reserva.solicitudes_especiales}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Datos del Cliente */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Datos del Cliente</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 font-medium">Nombre</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {reserva.huesped_nombre}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Email</p>
                <p className="text-lg font-semibold text-gray-900 break-all mt-1">
                  {reserva.huesped_email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Documento</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {reserva.huesped_documento}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 font-medium">Teléfono</p>
                <p className="text-lg font-semibold text-gray-900 mt-1">
                  {reserva.huesped_telefono || '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Card 4: Estados y Resumen */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            {/* Estados */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="rounded-lg p-4" style={{ backgroundColor: getStatusColors(
                reserva.estado === 'confirmada' ? 'confirmed' : reserva.estado === 'cancelada' ? 'cancelled' : 'pending',
                colors
              ).lightBackground }}>
                <p className="text-sm font-medium mb-2" style={{ color: getStatusColors(
                  reserva.estado === 'confirmada' ? 'confirmed' : reserva.estado === 'cancelada' ? 'cancelled' : 'pending',
                  colors
                ).backgroundColor }}>
                  Estado de Reserva
                </p>
                <p
                  className="text-lg font-bold"
                  style={{ color: getStatusColors(
                    reserva.estado === 'confirmada' ? 'confirmed' : reserva.estado === 'cancelada' ? 'cancelled' : 'pending',
                    colors
                  ).backgroundColor }}
                >
                  {formatearEstado(reserva.estado)}
                </p>
              </div>
              <div className="rounded-lg p-4" style={{ backgroundColor: getStatusColors(
                reserva.estado_pago === 'pagado' ? 'confirmed' : reserva.estado_pago === 'reembolsado' ? 'cancelled' : 'pending',
                colors
              ).lightBackground }}>
                <p className="text-sm font-medium mb-2" style={{ color: getStatusColors(
                  reserva.estado_pago === 'pagado' ? 'confirmed' : reserva.estado_pago === 'reembolsado' ? 'cancelled' : 'pending',
                  colors
                ).backgroundColor }}>
                  Estado de Pago
                </p>
                <p
                  className="text-lg font-bold"
                  style={{ color: getStatusColors(
                    reserva.estado_pago === 'pagado' ? 'confirmed' : reserva.estado_pago === 'reembolsado' ? 'cancelled' : 'pending',
                    colors
                  ).backgroundColor }}
                >
                  {formatearEstado(reserva.estado_pago)}
                </p>
              </div>
            </div>

            {/* Monto Total */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center">
                <p className="text-lg font-semibold text-gray-900">Monto Total</p>
                <p
                  className="text-3xl font-bold"
                  style={{ color: colors.primary }}
                >
                  ${montoTotal.toLocaleString('es-CL')}
                </p>
              </div>
            </div>
          </div>

          {/* Mensaje de Confirmación */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6 text-center">
            <p className="text-green-900 font-medium mb-2">
              ✅ Pago Confirmado
            </p>
            <p className="text-green-800 text-sm">
              Recibirás un correo de confirmación con los detalles de tu reserva.
            </p>
          </div>

          {/* Botón de Regreso */}
          <div className="flex gap-4">
            <button
              onClick={() => navigate('/')}
              className="flex-1 font-semibold text-white rounded-lg py-3 transition-all"
              style={{
                backgroundColor: colors.primary,
                opacity: 1
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              🏠 Volver al Inicio
            </button>
            <button
              onClick={() => window.print()}
              className="flex-1 font-semibold rounded-lg py-3 border-2 transition-all"
              style={{
                color: colors.primary,
                borderColor: colors.primary,
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = `${colors.primaryLight}20`)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              🖨️ Imprimir
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default ReservationConfirmationPage
